import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { STUDIO_PROJECTS, StudioProject } from '../../constants';
import GlassModal from './GlassModal';
import ConstructionCard from './ConstructionCard';
import '../../styles/studio.css';

interface StudioSectionProps {
    isActive: boolean;
    onBackToHero: () => void;
}

const StudioSection: React.FC<StudioSectionProps> = ({ isActive, onBackToHero }) => {
    const [selectedProject, setSelectedProject] = useState<StudioProject | null>(null);
    const [hoveredProject, setHoveredProject] = useState<string | null>(null);
    const sectionRef = useRef<HTMLDivElement>(null);

    // Smooth scroll effect using Lenis would be integrated here
    useEffect(() => {
        if (!isActive) return;

        // Initialize Lenis for smooth scrolling
        const initLenis = async () => {
            try {
                const Lenis = (await import('lenis')).default;
                const lenis = new Lenis({
                    duration: 1.2,
                    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                    smoothWheel: true,
                });

                function raf(time: number) {
                    lenis.raf(time);
                    requestAnimationFrame(raf);
                }
                requestAnimationFrame(raf);

                return () => lenis.destroy();
            } catch (e) {
                console.log('Lenis not available');
            }
        };

        initLenis();
    }, [isActive]);

    if (!isActive) return null;

    return (
        <motion.div
            ref={sectionRef}
            className="studio-section"
            initial={{ opacity: 1 }} // Mounted immediately
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
        // Remove duration transition to prevent fade blink
        >
            {/* Background decoration */}
            <div className="studio-bg">
                <div className="bg-gradient-orb" /> {/* Single ambient gradient */}
                <div className="bg-noise" />
            </div>

            {/* Header */}
            <header className="studio-header">
                <motion.button
                    className="back-button"
                    onClick={onBackToHero}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Back to Start</span>
                </motion.button>

                <div className="studio-branding">
                    <h1 className="studio-title">BRAND WEBSITES</h1>
                    <p className="studio-subtitle">Premium websites that make your brand unforgettable</p>
                </div>
            </header>

            {/* Projects Grid */}
            <section className="studio-projects">
                <motion.div
                    className="projects-grid"
                    initial="hidden"
                    animate="visible"
                    variants={{
                        hidden: { opacity: 0 },
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.15,
                                delayChildren: 0.2
                            }
                        }
                    }}
                >
                    {STUDIO_PROJECTS.map((project, index) => (
                        <motion.article
                            key={project.id}
                            className="studio-project-card group"
                            transition={{
                                delay: index * 0.1,
                                duration: 0.7, // Slower, more luxurious
                                ease: [0.25, 1, 0.5, 1] // Soft cubic bezier
                            }}
                            onMouseEnter={() => setHoveredProject(project.id)}
                            onMouseLeave={() => setHoveredProject(null)}
                            onClick={() => setSelectedProject(project)}
                            onMouseMove={(e) => {
                                if (window.matchMedia('(pointer: coarse)').matches) return;

                                const card = e.currentTarget;
                                const rect = card.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;

                                requestAnimationFrame(() => {
                                    card.style.setProperty('--x', `${x}px`);
                                    card.style.setProperty('--y', `${y}px`);
                                });
                            }}
                            style={{
                                '--accent-color': project.accentColor
                            } as React.CSSProperties}
                        >
                            {/* Background Image */}
                            {project.image && (
                                <div className="card-background-image">
                                    <img src={project.image} alt={project.title} />
                                    <div className="card-image-overlay" />
                                </div>
                            )}



                            {/* Card Content - Glass Overlay with gradual blur */}
                            <div className="card-content">
                                {/* Glass panel with text */}
                                <div className="card-glass-panel">
                                    <div className="card-header">
                                        <span className="card-category">{project.category.toUpperCase()}</span>
                                        <h3 className="card-title">{project.title}</h3>
                                        <p className="card-tagline">{project.tagline}</p>
                                    </div>

                                    <p className="card-description">{project.description}</p>

                                    <div className="card-features">
                                        {project.features.slice(0, 2).map((feature, i) => (
                                            <span key={i} className="feature-tag">{feature}</span>
                                        ))}
                                    </div>

                                    <motion.div
                                        className="card-cta"
                                        animate={{
                                            x: hoveredProject === project.id ? 5 : 0
                                        }}
                                    >
                                        <span>Click to Explore</span>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                    </motion.div>
                                </div>
                            </div>

                            {/* Accent border */}
                            <div className="card-accent-border" />
                        </motion.article>
                    ))}
                </motion.div>
            </section>

            {/* Profile / Expertise Section */}
            <section className="studio-profile">
                <div className="profile-container">
                    <div className="profile-header">
                        <h2 className="section-title">EXPERTISE & BACKGROUND</h2>
                        <div className="gold-divider" />
                    </div>

                    <div className="profile-grid">
                        {/* Left Column: Summary & Skills */}
                        <div className="profile-left">
                            <div className="profile-block">
                                <h3>PROFESSIONAL SUMMARY</h3>
                                <p>
                                    Full-stack developer with expertise in building AI-powered web applications,
                                    automation systems, and premium user interfaces. Experience spans modern web design,
                                    LLM integration, document processing automation, and industrial control systems.
                                    Strong focus on delivering production-ready solutions with clean architecture and user-centered design.
                                </p>
                            </div>

                            <div className="profile-block">
                                <h3>TECHNICAL ARSENAL</h3>
                                <div className="skill-category">
                                    <h4>AI/ML & AGENTIC</h4>
                                    <p>Gemini 2.5/3, LLM Orchestration, Prompt Engineering, Agentic Workflows</p>
                                </div>
                                <div className="skill-category">
                                    <h4>INDUSTRIAL CORE</h4>
                                    <p>HMI/SCADA, OPC UA, Industrial IoT, Real-time Telemetry</p>
                                </div>
                                <div className="skill-category">
                                    <h4>FULL STACK</h4>
                                    <p>React, Next.js, Node.js, Python, SQL, Supabase, PostgreSQL</p>
                                </div>
                                <div className="skill-category">
                                    <h4>CREATIVE & DEVOPS</h4>
                                    <p>GSAP, Framer Motion, Canvas API, Docker, Webhooks, REST APIs</p>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Experience */}
                        <div className="profile-right">
                            <h3>EXPERIENCE</h3>

                            <div className="experience-item">
                                <div className="exp-header">
                                    <h4>Founder & Technical Lead</h4>
                                    <span className="exp-date">2025 - Present</span>
                                </div>
                                <h5 className="exp-company">Operator Control Systems</h5>
                                <p className="exp-desc">
                                    Founded an industrial control systems engineering firm focusing on high-fidelity AI platforms and modern HMI/SCADA solutions. Spearheaded multi-industry deployment of Zero Trust industrial architectures.
                                </p>
                            </div>

                            <div className="experience-item">
                                <div className="exp-header">
                                    <h4>Founder & Lead Engineer</h4>
                                    <span className="exp-date">2025 - Present</span>
                                </div>
                                <h5 className="exp-company">Batch Systems</h5>
                                <p className="exp-desc">
                                    Established the web-development branch of Operator Control Systems, focusing on high-end design aesthetics and premium 3D web experiences.
                                </p>
                            </div>

                            <div className="experience-item">
                                <div className="exp-header">
                                    <h4>E-Commerce Entrepreneur</h4>
                                    <span className="exp-date">2021 - 2024</span>
                                </div>
                                <h5 className="exp-company">Watch Mafia & Multiple Brands</h5>
                                <p className="exp-desc">
                                    Founded and scaled multiple profitable brands leveraging advanced marketing automation. Successfully exited 5 e-commerce brands using data-driven decision making.
                                </p>
                            </div>

                            <div className="experience-item">
                                <div className="exp-header">
                                    <h4>BS Software Engineering</h4>
                                    <span className="exp-date">Education</span>
                                </div>
                                <h5 className="exp-company">B.Sc. Software Engineering</h5>
                                <p className="exp-desc">
                                    Specialized in software architecture, algorithms, and system design.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Skills & Contact Footer */}
            <footer className="studio-footer">
                <div className="footer-content">
                    <div className="footer-cta">
                        <h2>Ready to elevate your brand?</h2>
                        <p>Let's create something unforgettable together.</p>
                        <motion.a
                            href="mailto:isaadimughal@gmail.com"
                            className="contact-button"
                            whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(212, 175, 55, 0.4)' }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Get in Touch
                        </motion.a>
                    </div>

                    <div className="footer-info">
                        <p className="footer-name">SAAD MUGHAL</p>
                        <p className="footer-role">Full-Stack Developer & Brand Architect</p>
                    </div>
                </div>
            </footer>

            {/* Glass Modal for project viewing */}
            <AnimatePresence>
                {selectedProject && (
                    <GlassModal
                        project={selectedProject}
                        onClose={() => setSelectedProject(null)}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default StudioSection;
